/**
 * Email Account Stop Sync Modal
 * Confirmation modal for stopping sync (disables auto-sync for the account)
 */
import { useTranslation } from "react-i18next";
import { Button } from "@/components/base/buttons/button";
import { ModalOverlay, Modal, Dialog } from "@/components/application/modals/modal";
import { X, AlertCircle } from "@untitledui/icons";
import type { EmailAccountResponse } from "@/types/api/email";

interface EmailAccountStopSyncModalProps {
    account: EmailAccountResponse;
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
    isLoading?: boolean;
}

export const EmailAccountStopSyncModal = ({
    isOpen,
    onClose,
    onConfirm,
    isLoading = false,
}: EmailAccountStopSyncModalProps) => {
    const { t } = useTranslation(["pages:email-settings"]);

    if (!isOpen) return null;

    return (
        <ModalOverlay isOpen={isOpen} onOpenChange={(open) => !open && onClose()}>
            <Modal className="max-w-md">
                <Dialog>
                    <div className="w-full rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary">
                        {/* Header */}
                        <div className="mb-6 flex items-center justify-between">
                            <h2 className="text-lg font-semibold text-primary">
                                {t("stopSyncConfirm.title")}
                            </h2>
                            <Button
                                onClick={onClose}
                                color="tertiary"
                                size="sm"
                                iconLeading={X}
                                aria-label={t("actions.close")}
                            />
                        </div>

                        {/* Icon */}
                        <div className="mb-4 flex justify-center">
                            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-warning-secondary">
                                <AlertCircle className="h-6 w-6 text-fg-warning-secondary" />
                            </div>
                        </div>

                        {/* Message */}
                        <p className="mb-6 text-center text-sm text-tertiary">
                            {t("stopSyncConfirm.message")}
                        </p>

                        {/* Actions */}
                        <div className="flex justify-end gap-3">
                            <Button
                                onClick={onClose}
                                isDisabled={isLoading}
                                color="secondary"
                                size="sm"
                            >
                                {t("stopSyncConfirm.cancel")}
                            </Button>
                            <Button
                                onClick={onConfirm}
                                isDisabled={isLoading}
                                isLoading={isLoading}
                                color="primary-destructive"
                                size="sm"
                            >
                                {isLoading ? t("stopSyncConfirm.stopping") : t("stopSyncConfirm.confirm")}
                            </Button>
                        </div>
                    </div>
                </Dialog>
            </Modal>
        </ModalOverlay>
    );
};
